import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Input, Modal, ModalContent, ModalFooter, RadioGroup } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AssignableDashboardsDocument,
    DeclareDashboardDocument,
    UpdateDashboardDocument,
} from '../../graphql/__generated__/types';
import { catalogueRefusalOf } from './refusals';

export type DashboardDialogMode = 'declare' | 'rename';

interface Props {
    mode: DashboardDialogMode;
    accountId: string;
    accountName: string;
    entry?: { id: string; name: string } | null;
    onClose: () => void;
}

// Renaming touches the name and nothing else: no query returns DashboardEntry, so a form
// offering the other fields would open empty and overwrite values the operator never saw.
// There is no URL field, and must never be one: a Metabase address is a bearer; only the
// entry (identifier and perimeter parameter) is declared and the API derives the link.
const DashboardDialog = ({ mode, accountId, accountName, entry, onClose }: Props) => {
    const declaring = mode === 'declare';

    const [name, setName] = useState(entry?.name ?? '');
    const [metabaseId, setMetabaseId] = useState('');
    // id_client is the account parameter's slug on every portal dashboard so far; pre-filled but editable.
    const [organizationParam, setOrganizationParam] = useState('id_client');
    // A global dashboard signs without lock and shows the whole aggregate, so it has no
    // account parameter: the field disappears and the API refuses the two together.
    const [isGlobal, setIsGlobal] = useState(false);

    // The catalogue is read by this panel and by step 3 of onboarding; evicting the field
    // stops the journey being served a stale cached empty for accounts it asked about earlier.
    const options = {
        refetchQueries: [AssignableDashboardsDocument],
        awaitRefetchQueries: true,
        update: (cache: { evict: (o: object) => boolean; gc: () => unknown }) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'assignableDashboards' });
            cache.gc();
        },
    };

    const [declare, declareState] = useMutation(DeclareDashboardDocument, options);
    const [rename, renameState] = useMutation(UpdateDashboardDocument, options);

    const { loading, error } = declaring ? declareState : renameState;
    const done = declaring ? declareState.data?.declareDashboard : renameState.data?.updateDashboard;

    const trimmedName = name.trim();
    const parsedMetabaseId = Number(metabaseId.trim());
    const metabaseIdValid = metabaseId.trim().length > 0 && Number.isInteger(parsedMetabaseId) && parsedMetabaseId > 0;
    // The account parameter is required only for a scoped dashboard; a global one locks nothing.
    const perimeterMissing = !isGlobal && organizationParam.trim().length === 0;

    const blocked = declaring
        ? trimmedName.length === 0 || !metabaseIdValid || perimeterMissing
        : trimmedName.length === 0 || trimmedName === entry?.name;

    const submit = () => {
        const run = declaring
            ? declare({
                  variables: {
                      organizationId: accountId,
                      name: trimmedName,
                      metabaseDashboardId: parsedMetabaseId,
                      organizationParam: isGlobal ? null : organizationParam.trim(),
                      // The portal restricts by account only, so no market parameter: null, never an empty string.
                      marketParam: null,
                      isGlobal,
                  },
              })
            : rename({ variables: { dashboardId: entry?.id as string, name: trimmedName } });
        // Swallow the rejection: the refusal is read off error below, otherwise every refusal logs an unhandled rejection.
        run.catch(() => {});
    };

    const title = declaring
        ? c('Dashboard catalogue').t`Declare a dashboard`
        : c('Dashboard catalogue').t`Rename this dashboard`;

    return (
        <Modal open onClose={onClose} title={title} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">{accountName}</p>

                    {done ? (
                        <Alert
                            type="success"
                            title={
                                declaring
                                    ? c('Dashboard catalogue').t`The entry is declared.`
                                    : c('Dashboard catalogue').t`The entry is renamed.`
                            }
                        >
                            <p className="text-sm">
                                {declaring
                                    ? c('Dashboard catalogue')
                                          .t`It is now in this account's catalogue. Grant it person by person, from an onboarding or a user's sheet; nobody holds it yet.`
                                    : c('Dashboard catalogue')
                                          .t`The label is changed. It is what the client reads, so the people already holding this dashboard will see the new one.`}
                            </p>
                            {/* The only place these values can be shown: no query returns them, so a reload cannot. */}
                            {declaring && (
                                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
                                    <dt className="text-text-600">{c('Dashboard catalogue').t`Metabase id`}</dt>
                                    <dd>{done.metabaseDashboardId}</dd>
                                    <dt className="text-text-600">{c('Dashboard catalogue').t`Account parameter`}</dt>
                                    <dd>
                                        {declareState.data?.declareDashboard?.isGlobal
                                            ? c('Dashboard catalogue').t`Global: no account filter`
                                            : done.organizationParam}
                                    </dd>
                                </dl>
                            )}
                        </Alert>
                    ) : (
                        <>
                            {!declaring && (
                                <Alert type="info">
                                    {c('Dashboard catalogue')
                                        .t`Only the label can be changed here. To change the Metabase identifier or the perimeter parameters, delete this entry and declare it again.`}
                                </Alert>
                            )}

                            {declaring ? (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field
                                        id="dashboard-name"
                                        label={c('Dashboard catalogue').t`Label read by the client`}
                                    >
                                        <Input
                                            id="dashboard-name"
                                            value={name}
                                            onValue={setName}
                                            placeholder={c('Dashboard catalogue').t`Consumption 2026`}
                                        />
                                    </Field>

                                    <Field
                                        id="dashboard-metabase-id"
                                        label={c('Dashboard catalogue').t`Metabase dashboard identifier`}
                                    >
                                        <Input
                                            id="dashboard-metabase-id"
                                            value={metabaseId}
                                            onValue={setMetabaseId}
                                            inputMode="numeric"
                                            placeholder="42"
                                            error={metabaseId.trim().length > 0 && !metabaseIdValid}
                                        />
                                        {metabaseId.trim().length > 0 && !metabaseIdValid && (
                                            <p className="mt-1.5 text-sm text-danger-600">
                                                {c('Dashboard catalogue').t`A positive whole number is expected here.`}
                                            </p>
                                        )}
                                    </Field>

                                    <div className="sm:col-span-2 flex flex-col gap-1.5">
                                        <span className="text-sm font-medium text-text-900">
                                            {c('Dashboard catalogue').t`Data shown`}
                                        </span>
                                        <RadioGroup
                                            category="card"
                                            name="dashboard-perimeter"
                                            value={isGlobal ? 'global' : 'scoped'}
                                            onValue={(value: string) => setIsGlobal(value === 'global')}
                                            options={[
                                                {
                                                    value: 'scoped',
                                                    label: c('Dashboard catalogue').t`Scoped by account`,
                                                    description: c('Dashboard catalogue')
                                                        .t`Everyone who holds it sees only their own account's figures, through a locked parameter.`,
                                                },
                                                {
                                                    value: 'global',
                                                    label: c('Dashboard catalogue').t`Global (all data)`,
                                                    description: c('Dashboard catalogue')
                                                        .t`An aggregate shown whole, with no account filter. For dashboards that carry no nominative data.`,
                                                },
                                            ]}
                                        />
                                    </div>

                                    {!isGlobal && (
                                        <div className="sm:col-span-2">
                                            <Field
                                                id="dashboard-organization-param"
                                                label={c('Dashboard catalogue')
                                                    .t`Parameter carrying the account (required)`}
                                            >
                                                <Input
                                                    id="dashboard-organization-param"
                                                    value={organizationParam}
                                                    onValue={setOrganizationParam}
                                                    placeholder={c('Dashboard catalogue')
                                                        .t`The account's source identifier`}
                                                />
                                            </Field>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Field id="dashboard-name" label={c('Dashboard catalogue').t`Label read by the client`}>
                                    <Input
                                        id="dashboard-name"
                                        value={name}
                                        onValue={setName}
                                        placeholder={c('Dashboard catalogue').t`Consumption 2026`}
                                    />
                                </Field>
                            )}

                            {/* Shown only once the operator has started composing, not on an untouched form. */}
                            {declaring && perimeterMissing && trimmedName.length > 0 && (
                                <p className="text-sm text-warning-600">
                                    {c('Dashboard catalogue')
                                        .t`The account parameter is still empty, so this entry cannot be declared: it would be granted and never open anything.`}
                                </p>
                            )}

                            {declaring && isGlobal && (
                                <Alert type="info">
                                    {c('Dashboard catalogue')
                                        .t`Attention: this dashboard is signed with no lock and shows every account's data whole. Reserve it for aggregates with no nominative data.`}
                                </Alert>
                            )}

                            {error && <RefusalNotice refusal={catalogueRefusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button category="default" loading={loading} disabled={blocked} onClick={submit}>
                            {declaring
                                ? c('Dashboard catalogue').t`Declare the dashboard`
                                : c('Dashboard catalogue').t`Rename`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

const Field = ({
    id,
    label,
    hint,
    children,
}: {
    id: string;
    label: string;
    hint?: string;
    children: React.ReactNode;
}) => (
    <div>
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-text-900">
            {label}
        </label>
        {children}
        {hint && <p className="mt-1.5 text-xs text-text-600">{hint}</p>}
    </div>
);

export default DashboardDialog;
